import { useState, useContext } from 'react';
import { withRouter } from '../../utils/WithRouter';
import { gql, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { Link, useNavigate } from 'react-router-dom';
import { Box, IconButton, Button, Tooltip } from '@mui/material';
import FlagRounded from '@mui/icons-material/FlagRounded';
import { IComment, ICommentOne } from '../../types/comment';
import {
  numToCurrency,
  downloadImages,
  getOutdoorSize,
  getOutdoorType,
  getSalesStatusCodes,
  netHST,
  buildWhatsAppShareUrl,
  buildEmailShareUrl,
  isIos,
} from '../../utils/Functions';
import { libertyProjects } from '../../utils/Constants';
import PDFGenerator from './PDFGenerator';
import ShareRecipientDialog, { IShareRecipient, ShareMethod } from './ShareRecipientDialog';
import ShareLinkDialog from './ShareLinkDialog';
import { Flex } from '../../commonStyles';
import { useSelector } from 'react-redux';
import { selectProject } from '../../features/project/projectSlice';
import { selectUser } from '../../features/auth/authSlice';
import { accessAllowed } from '../../features/project/projectHooks';
import { useAppDispatch } from '../../app/hooks';
import { showSuccessSnackbar, showErrorSnackbar } from '../../features/snackbar/snackbarSlice';
import { IUnitDataGridCard } from '../../types/unit';
import LoadingWrapper from './LoadingWrapper';
import { handleModal } from '../../features/modal/modalSlice';
import { PriceGridContext } from '../../context/PriceGridContext';
import { Folder } from '@mui/icons-material';
import { useCreateActivity } from '../../features/activity/activityHooks';
import { useUploadSharedPdf } from '../../features/share/shareHooks';

const FloorplanModal = () => {
  const storeDispatch = useAppDispatch();
  const project = useSelector(selectProject);
  const user = useSelector(selectUser);
  const navigate = useNavigate();

  const { setModalType, setUnit, unit, draft, units, setUnits } = useContext(PriceGridContext);
  const createActivity = useCreateActivity();
  const uploadSharedPdf = useUploadSharedPdf();

  const [latestComment, setLatestComment] = useState<IComment | null>(null);
  const [shareDialogOpen, setShareDialogOpen] = useState<boolean>(false);
  const [pendingShare, setPendingShare] = useState<{ url: string; method: ShareMethod } | null>(null);

  const { loading } = useQuery<IUnitDataGridCard>(GETUNIT, {
    variables: { _id: unit._id },
    onCompleted: (data) => {
      setUnit({
        ...unit,
        getUrl: data.unitById.getUrl,
      });
    },
  });

  const [shareFloorplan] = useLazyQuery(SHAREFLOORPLAN, {
    fetchPolicy: 'no-cache',
    variables: { filter: { project: project._id, modelType: unit?.modelType } },
    onCompleted: (data) => {},
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const [updateUnit] = useMutation(UPDATEUNIT, {
    onCompleted: (data) => {
      let updatedSelectedUnits = units.map((displayedUnit: any) => {
        if (displayedUnit._id === data.unitUpdateById.record._id) {
          return {
            ...displayedUnit,
            flag: data.unitUpdateById.record.flag,
          };
        } else return displayedUnit;
      });

      setUnit(data.unitUpdateById.record);
      setUnits(updatedSelectedUnits);
      storeDispatch(showSuccessSnackbar('Unit Updated!'));
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const [setPrepared] = useMutation(UPDATEUNIT, {
    onCompleted: (data) => {},
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  useQuery<ICommentOne>(GETCOMMENT, {
    variables: { filter: { unit: unit?._id }, sort: '_ID_DESC' },
    onCompleted: (data) => {
      setLatestComment(data.commentOne);
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const handleFlag = (id: string) => {
    updateUnit({
      variables: {
        _id: id,
        record: {
          flag: !unit?.flag,
        },
      },
    });
  };

  // Creating a deal
  const updateStatus = () => {
    setModalType('');
    storeDispatch(handleModal(false));
    if (unit?.status !== 'P') {
      setPrepared({
        variables: {
          _id: unit?._id,
          record: {
            status: 'P',
          },
        },
      });
    }
  };

  const handleDownloadFloorplan = () => {
    shareFloorplan().then((res) => {
      if (res.data.marketingFloorPlansOne) {
        const element = document.createElement('a');
        element.download = res.data.marketingFloorPlansOne.pdfGetUrl
          ? res.data.marketingFloorPlansOne.pdfGetUrl
          : res.data.marketingFloorPlansOne.getUrl;
        element.href = res.data.marketingFloorPlansOne.pdfGetUrl
          ? res.data.marketingFloorPlansOne.pdfGetUrl
          : res.data.marketingFloorPlansOne.getUrl;
        element.target = '_blank';
        element.click();
      } else {
        storeDispatch(showErrorSnackbar('No Marketing Floorplan Uploaded'));
      }
    });
  };

  // The merged floorplan PDF is uploaded to the bucket's shared/ folder by the backend, and the link
  // it returns is what gets sent - WhatsApp/mailto links can't attach a file. The recipient captured
  // in the dialog is for the activity log (and the email "to"), not for pre-selecting a WhatsApp chat.
  const handleRecipientShare = (recipient: IShareRecipient) => {
    const { method } = recipient;
    setShareDialogOpen(false);
    shareFloorplan().then((res) => {
      if (res.data.marketingFloorPlansOne) {
        const floorplanUrl = res.data.marketingFloorPlansOne.pdfGetUrl
          ? res.data.marketingFloorPlansOne.pdfGetUrl
          : res.data.marketingFloorPlansOne.getUrl;
        const basePrice = project.netHst ? netHST(unit.basePrice, project) : unit.basePrice;
        const recipientLabel = recipient.contact ? `${recipient.name} (${recipient.contact})` : recipient.name;
        const marketing = [{ ...unit, marketingFloorPlan: floorplanUrl }];

        const sendLink = async (pdf: File) => {
          const url = await uploadSharedPdf(pdf, recipient.name, project._id!);
          if (!url) return false;

          const firstName = recipient.name.trim().split(/\s+/)[0];
          const message = `Hello ${firstName}, please see the link to the floorplans\n${url}`;
          const ios = isIos();
          const shareUrl =
            method === 'whatsapp'
              ? buildWhatsAppShareUrl(recipient.contact, message, ios)
              : buildEmailShareUrl(
                  recipient.contact,
                  `${project.name} - Suite ${unit.suite} Floorplan`,
                  message,
                  libertyProjects.includes(project._id!) ? project.email : undefined
                );
          if (ios) {
            setPendingShare({ url: shareUrl, method });
          } else {
            window.location.href = shareUrl;
          }
          return true;
        };

        downloadImages(marketing, project.email, project.name, true, project._id, project, sendLink).then((shared: boolean) => {
          if (shared && process.env.REACT_APP_ENV !== 'local') {
            createActivity({
              project: project._id,
              user: user._id,
              units: [unit._id],
              title: `Units Shared - ${method === 'whatsapp' ? 'WhatsApp' : 'Email'}`,
              content: `${unit.suite} - ${numToCurrency.format(basePrice)} → Shared with ${recipientLabel} via ${
                method === 'whatsapp' ? 'WhatsApp' : 'Email'
              }`,
            });
          }
        });
      } else {
        storeDispatch(showErrorSnackbar('No Marketing Floorplan Uploaded'));
      }
    });
  };

  return (
    <>
    <Box
      sx={{
        backgroundColor: '#fff',
        paddingTop: '10px',
        paddingBottom: '40px',
        paddingRight: '30px',
        paddingLeft: '30px',
        margin: '0 auto',
        border: unit?.flag ? '3px solid red' : '3px solid black',
        '@media (max-height: 600px)': {
          height: '560px',
        },
        '@media (max-width: 500px)': {
          width: '280px',
          overflowY: 'auto',
        },
      }}
    >
      {!loading ? (
        <>
          <Flex>
            <Box
              sx={{
                margin: '0 auto',
                marginRight: 'auto',
              }}
            >
              <h1>Suite {unit?.suite}</h1>
            </Box>
            <Box
              sx={{
                marginTop: '10px',
              }}
            >
              <Tooltip title={unit.flag ? 'Unflag Unit' : 'Flag Unit'}>
                <IconButton aria-controls="fade-menu" aria-haspopup="true">
                  <FlagRounded color="error" onClick={() => handleFlag(unit?._id!)} />
                </IconButton>
              </Tooltip>
              <Tooltip title="View Unit">
                <IconButton aria-controls="fade-menu" aria-haspopup="true" sx={{ alignSelf: 'center' }}>
                  <Folder
                    color="primary"
                    onClick={() => {
                      storeDispatch(handleModal(false));
                      navigate(`/${project._id}/dashboard/${unit?._id}`);
                    }}
                  />
                </IconButton>
              </Tooltip>
            </Box>
          </Flex>
          <Flex
            sx={{
              '@media (max-width: 600px)': {
                flexDirection: 'column',
              },
            }}
          >
            <Flex
              sx={(theme) => ({
                maxHeight: '540px',
                maxWidth: '500px',
              })}
            >
              {unit?.getUrl ? <PDFGenerator merge={unit.getUrl} type={'url'} floorPlan={true} height="100%" /> : null}
            </Flex>
            <Box
              sx={(theme) => ({
                [theme.breakpoints.down('sm')]: {
                  marginLeft: 0,
                  width: 'auto',
                },
                ml: 2,
              })}
            >
              <h3
                style={{
                  margin: '8px 0',
                }}
              >
                Suite Information
              </h3>
              <Box sx={{ my: 1 }}>
                Suite Number: <strong>{unit?.suite}</strong>
              </Box>
              <Box sx={{ my: 1 }}>
                Legal Level: <strong>{unit?.level}</strong>
              </Box>
              <Box sx={{ my: 1 }}>
                Legal Unit: <strong>{unit?.unit}</strong>
              </Box>
              <Box sx={{ my: 1 }}>
                Base Price{project.netHst ? ' (Net HST)' : ''}:{' '}
                <strong>{numToCurrency.format(project.netHst ? netHST(unit?.basePrice, project) : unit?.basePrice)}</strong>
              </Box>
              <Box sx={{ my: 1 }}>
                Model Type: <strong>{unit?.modelType}</strong>
              </Box>
              <Box sx={{ my: 1 }}>
                Unit Type: <strong>{unit?.unitType}</strong>
              </Box>
              <Box sx={{ my: 1 }}>
                Exposure: <strong>{unit?.exposure}</strong>
              </Box>
              <Box sx={{ my: 1 }}>
                Number of Bathrooms: <strong>{unit?.bathroom}</strong>
              </Box>
              <Box sx={{ my: 1 }}>
                Size: <strong>{unit?.size} sqft</strong>
              </Box>
              <Box sx={{ my: 1 }}>
                Outdoor Size: <strong>{getOutdoorSize(unit?.outdoor)} sqft</strong>
              </Box>
              {getOutdoorType(unit.outdoor) ? (
                <Box sx={{ my: 1 }}>
                  Outdoor Type: <strong>{getOutdoorType(unit.outdoor)}</strong>
                </Box>
              ) : null}
              <Box sx={{ my: 1 }}>
                Price Per Square Foot: &nbsp;<strong>{numToCurrency.format(unit?.basePrice / unit?.size!)}</strong>
              </Box>
              {unit.rental ? (
                <Box sx={{ my: 1 }}>
                  Rental: &nbsp;<strong>{numToCurrency.format(unit?.rental)}</strong>
                </Box>
              ) : null}
              <Box sx={{ my: 1 }}>
                Status: <strong>{unit?.status}</strong>
              </Box>
              <Box sx={{ my: 1 }}>
                Allocation:{' '}
                <strong>{unit?.allocation ? unit?.allocation.fullName : unit?.tempAllocation ? unit?.tempAllocation : 'None'}</strong>
              </Box>

              <div style={{ margin: '10px 0 10px 0' }}>
                {!draft ? (
                  <Button sx={{ mr: 1 }} variant="contained" color="primary" onClick={() => setShareDialogOpen(true)}>
                    Share
                  </Button>
                ) : null}
                {!draft ? (
                  <Button sx={{ mr: 1 }} variant="contained" color="primary" onClick={() => handleDownloadFloorplan()}>
                    Marketing
                  </Button>
                ) : null}
                {accessAllowed(user, project?._id!, 'createDeal') && !draft ? (
                  !getSalesStatusCodes(project).includes(unit?.status) &&
                  unit?.status !== 'O' &&
                  unit?.status !== 'S' &&
                  unit?.status !== 'P' &&
                  unit?.status !== 'UT' ? (
                    <Link
                      to={`/${project._id}/dashboard/create-deal/${unit?._id}/form`}
                      style={{ textDecoration: 'none', color: 'inherit' }}
                    >
                      <Button variant="contained" color="primary" onClick={() => updateStatus()}>
                        New Deal
                      </Button>
                    </Link>
                  ) : (
                    <Link to={`/${project._id}/dashboard/${unit?._id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                      <Button variant="contained" color="primary">
                        View Active Deal
                      </Button>
                    </Link>
                  )
                ) : null}
              </div>
              {latestComment ? (
                <Box sx={{ my: 1 }}>
                  <div>
                    <em>{latestComment.comment}</em>
                  </div>
                  <Box
                    sx={{
                      marginTop: '20px',
                      textAlign: 'right',
                    }}
                  >
                    - {latestComment.user.fullName}
                  </Box>
                </Box>
              ) : (
                <Box sx={{ my: 1 }}>
                  <em>There are currently no notes for this unit</em>
                </Box>
              )}
            </Box>
          </Flex>
        </>
      ) : (
        <Box sx={{ width: '600px' }}>
          <LoadingWrapper title="Loading..." modal={false} />
        </Box>
      )}
    </Box>
    <ShareRecipientDialog open={shareDialogOpen} suites={[unit?.suite]} onClose={() => setShareDialogOpen(false)} onSubmit={handleRecipientShare} />
    <ShareLinkDialog share={pendingShare} onClose={() => setPendingShare(null)} />
    </>
  );
};

const GETUNIT = gql`
  query unitById($_id: MongoID!) {
    unitById(_id: $_id) {
      suite
      getUrl
    }
  }
`;

const UPDATEUNIT = gql`
  mutation unitUpdateById($_id: MongoID!, $record: UpdateByIdUnitInput!) {
    unitUpdateById(_id: $_id, record: $record) {
      record {
        _id
        suite
        modelType
        unitType
        bathroom
        size
        basePrice
        level
        exposure
        outdoor {
          name
          size
        }
        col
        row
        status
        getUrl
        flag
        tempAllocation
        history {
          type
          description
          timestamp
          user {
            _id
            fullName
          }
          _id
        }
        stackRow
        worksheets {
          _id
        }
      }
    }
  }
`;

const GETCOMMENT = gql`
  query commentOne($filter: FilterFindOneCommentInput, $sort: SortFindOneCommentInput) {
    commentOne(filter: $filter, sort: $sort) {
      _id
      user {
        _id
        fullName
      }
      comment
      createdAt
      updatedAt
    }
  }
`;

const SHAREFLOORPLAN = gql`
  query marketingFloorPlansOne($filter: FilterFindOnemarketingFloorPlansInput) {
    marketingFloorPlansOne(filter: $filter) {
      _id
      getUrl
      pdfGetUrl
      project {
        _id
      }
      modelType
      size
    }
  }
`;

export default withRouter(FloorplanModal);
